import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { DefaultTheme } from 'styled-components';
import styled, { css } from 'styled-components';
import { Button, ButtonVariants } from '../Button';
import { Box, HStack } from '../Core';
import { Icon, IconName } from '../Icons';
import { Text } from '../Text';

export interface SlideToConfirmProps {
  onConfirm: () => boolean | Promise<boolean>;
  text: string;
  testId?: string;
  trackText?: string;
  variant: ButtonVariants;
}

const THUMB_WIDTH = 96;
const CONFIRM_THRESHOLD = 0.92;

export function SlideToConfirm({
  onConfirm,
  text,
  testId = 'mobile-order-confirmation-slide',
  trackText,
  variant,
}: SlideToConfirmProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const isConfirmingRef = useRef(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const getMaxX = useCallback(() => {
    const trackWidth = trackRef.current?.getBoundingClientRect().width ?? 0;
    const thumbWidth = thumbRef.current?.getBoundingClientRect().width ?? THUMB_WIDTH;

    return Math.max(trackWidth - thumbWidth, 0);
  }, []);

  const getPointerX = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const trackLeft = trackRef.current?.getBoundingClientRect().left ?? 0;
      const thumbWidth = thumbRef.current?.getBoundingClientRect().width ?? THUMB_WIDTH;
      const pointerX = event.clientX - trackLeft - thumbWidth / 2;

      return Math.min(Math.max(pointerX, 0), getMaxX());
    },
    [getMaxX]
  );

  const reset = useCallback(() => {
    isConfirmingRef.current = false;
    setIsFinished(false);
    setDragX(0);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (isConfirmingRef.current) {
      return false;
    }

    isConfirmingRef.current = true;
    setIsFinished(true);
    setDragX(getMaxX());

    try {
      const confirmed = await onConfirm();
      if (!confirmed) {
        reset();
      }

      return confirmed;
    } catch (error) {
      reset();
      throw error;
    }
  }, [getMaxX, onConfirm, reset]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (isFinished) {
        return;
      }

      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDragging(true);
      setDragX(getPointerX(event));
    },
    [getPointerX, isFinished]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isDragging || isFinished) {
        return;
      }

      setDragX(getPointerX(event));
    },
    [getPointerX, isDragging, isFinished]
  );

  const handlePointerEnd = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isDragging || isFinished) {
        return;
      }

      setIsDragging(false);

      const maxX = getMaxX();
      const releaseX = getPointerX(event);
      if (maxX > 0 && releaseX >= maxX * CONFIRM_THRESHOLD) {
        handleConfirm();
        return;
      }

      setDragX(0);
    },
    [getMaxX, getPointerX, handleConfirm, isDragging, isFinished]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      handleConfirm();
    },
    [handleConfirm]
  );

  if (import.meta.env.VITE_IS_TEST_ENVIRONMENT === 'true') {
    return (
      <Button data-testid={testId} type="button" onClick={handleConfirm} variant={variant}>
        {text}
      </Button>
    );
  }

  const maxX = getMaxX();
  const progress = maxX > 0 ? dragX / maxX : 0;

  return (
    <Track ref={trackRef} data-testid={testId}>
      <TrackText progress={progress}>
        <Text color="colorTextSubtle">{trackText ?? getDefaultTrackText(variant)}</Text>
      </TrackText>
      <TrackFill progress={progress} variant={variant} />
      <Thumb
        ref={thumbRef}
        role="button"
        tabIndex={0}
        data-testid="mobile-order-confirmation-slide-thumb"
        x={dragX}
        isDragging={isDragging}
        variant={variant}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
      >
        {text}
        <Icon icon={IconName.ArrowRightCircle} />
      </Thumb>
    </Track>
  );
}

function getDefaultTrackText(variant: ButtonVariants) {
  if (variant === ButtonVariants.Negative) {
    return 'Slide to sell';
  }
  if (variant === ButtonVariants.Primary) {
    return 'Slide to confirm';
  }

  return 'Slide to buy';
}

const Track = styled(Box)`
  position: relative;
  height: 48px;
  width: 100%;
  overflow: hidden;
  touch-action: pan-y;
  border-radius: ${({ theme }) => theme.borderRadiusLarge + theme.spacing2 * 2}px;
  border: 2px solid ${({ theme }) => theme.colors.gray['010']};
  background: ${({ theme }) => theme.colors.gray['010']};
`;

const TrackText = styled(Box)<{ progress: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: ${({ progress }) => Math.max(0, 1 - progress * 1.4)};
  transition: opacity 50ms linear;
`;

const TrackFill = styled(Box)<{ progress: number; variant: ButtonVariants }>`
  position: absolute;
  inset: 0;
  opacity: ${({ progress }) => Math.max(0, progress - 0.1)};
  transition: opacity 150ms ease;

  ${({ variant, theme }) => getTrackFillStyles(variant, theme)}
`;

const Thumb = styled(HStack)<{ x: number; isDragging: boolean; variant: ButtonVariants }>`
  position: absolute;
  height: 100%;
  min-width: ${THUMB_WIDTH}px;
  padding: ${({ theme }) => `${theme.spacing8}px ${theme.spacing16}px`};
  gap: ${({ theme }) => theme.spacing4}px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  border-radius: ${({ theme }) => theme.borderRadiusLarge}px;
  transform: translateX(${({ x }) => x}px);
  transition: ${({ isDragging }) => (isDragging ? 'none' : 'transform 350ms ease')};

  ${({ variant, isDragging, theme }) => {
    if (variant === ButtonVariants.Negative) {
      return css`
        background: ${isDragging ? theme.backgroundNegativeButtonFocus : theme.backgroundNegativeButton};
        color: ${theme.colorTextNegativeButton};
      `;
    }

    if (variant === ButtonVariants.Primary) {
      return css`
        background: ${isDragging ? theme.backgroundPrimaryButtonFocus : theme.backgroundPrimaryButton};
        color: ${theme.colorTextPrimaryButton};
      `;
    }

    return css`
      background: ${isDragging ? theme.backgroundPositiveButtonFocus : theme.backgroundPositiveButton};
      color: ${theme.colorTextPositiveButton};
    `;
  }}
`;

function getTrackFillStyles(variant: ButtonVariants, theme: DefaultTheme) {
  if (variant === ButtonVariants.Negative) {
    return css`
      background: linear-gradient(0.25turn, ${theme.colors.red.mute}, ${theme.colors.red.lighten});
    `;
  }

  if (variant === ButtonVariants.Primary) {
    return css`
      background: linear-gradient(0.25turn, ${theme.colors.blue.mute}, ${theme.colors.blue.lighten});
    `;
  }

  return css`
    background: linear-gradient(0.25turn, ${theme.colors.green.mute}, ${theme.colors.green.lighten});
  `;
}
